import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { SummaryElement } from "../atoms/summary-element";
import { ImageSlideshowElement } from "../basic/image-slideshow-element";
import { ResponsiveImage, StoryElement, SocialShare, LazyLoadImages, ImageGalleryElement } from "@quintype/components";
import { ImageLightbox } from "./lightbox/image-lightbox";
import { scrollInViewCard } from "../../../utils/scroll-in-view";
import { PromotionalMessageElement } from "../basic/promotional-message-element";
import { getSubType, selectedStoryTemplate } from "../../utils.js";
import { Blurb } from "../atoms/blurb/index";
import { AlsoRead } from "../atoms/also-read/index";
import { Quote } from "../atoms/quote/index";
import { ShareTemplate } from "../molecules/share/index";
import { BlockQuote } from "../atoms/block-quote/index";
import "./photo-story-card.module.css";
import { QuestionAnswer } from "../atoms/q-and-a/index";
import { Attachment } from "../atoms/attachment/index";
import { truncate } from "../../../utils/truncate-string";
import { CustomLink } from "../custom-link";

const MainImageWrapper = ({ imageElement, story, card, socialShareGrey, primaryColor, children, cdn }) => (
  <>
    <figure styleName="image" data-testid="photo-story-image">
      <LazyLoadImages margin={"0px"}>
        <ImageLightbox
          imageSlug={imageElement["image-s3-key"]}
          cdn={cdn}
          template={ResponsiveImage}
          element={imageElement}
          key={imageElement.id}
          metadata={imageElement["metadata"]}
          slug={imageElement["image-s3-key"]}
          defaultWidth={480}
          widths={[250, 480, 640]}
          imgParams={{ auto: ["format", "compress"] }}
          alt={imageElement.title}
        />
      </LazyLoadImages>
    </figure>
    <div styleName="story-element-wrapper slideStyle">
      <div styleName="wrapper">
        {imageElement["image-attribution"] && (
          <div styleName="attribution">Credits: {imageElement["image-attribution"]}</div>
        )}
        <SocialShare
          url={`${get(story, ["slug"], "")}?cardId=${get(card, ["id"], -1)}`}
          title={
            get(card, ["metadata"], {})
              ? get(card, ["metadata", "social-share", "title"], "")
              : get(story, ["title"], "")
          }
          template={ShareTemplate}
          grey={socialShareGrey || ""}
          cardShare={true}
          hashtags={get(story, ["tags"], "")
            .map(tag => tag.name)
            .toString()}
          primaryColor={primaryColor || ""}
        />
      </div>
      {imageElement.title && (
        <figcaption dangerouslySetInnerHTML={{ __html: imageElement.title }} styleName="caption" />
      )}
      {children}
    </div>
  </>
);

MainImageWrapper.propTypes = {
  imageElement: PropTypes.object,
  children: PropTypes.node,
  socialShareGrey: PropTypes.bool,
  primaryColor: PropTypes.string,
  story: PropTypes.object,
  card: PropTypes.object,
  cdn: PropTypes.string
};

class PhotoStoryCard extends React.Component {
  constructor(props) {
    super(props);
    this.cardRef = null;
  }

  componentDidMount() {
    scrollInViewCard(this.props, this.cardRef);
  }
  getStoryElement(element, index) {
    const storyTemp = get(this.props, ["story"]);
    const { "attachment-element": attachmentElement = "default" } = get(
      this.props,
      ["config", "theme-attributes", "story-elements"],
      {}
    );
    const elementAlignment = get(
      this.props,
      ["card", "metadata", "attributes", "elementalignment", 0],
      ""
    ).toLowerCase();
    const subType = getSubType(element);
    switch (subType) {
      case "summary":
        return <SummaryElement element={element} key={element.id} />;
      case "blockquote":
        return (
          <div key={index}>
            <BlockQuote element={element} key={element.id} index={index} elementAlignment={elementAlignment} />
          </div>
        );
      case "image-gallery":
        const template = get(element, ["metadata", "type"]) === "gallery" ? ImageGalleryElement : ImageSlideshowElement;
        return (
          <div key={index}>
            <ImageLightbox
              template={template}
              element={element}
              key={element.id}
              story={this.props.story}
              cdn={this.props.cdn}
            />
          </div>
        );
      case "promotional-message":
        return <PromotionalMessageElement element={element} key={element.id} externalLink />;
      case "blurb":
        return <Blurb element={element} key={element.id} />;
      case "quote":
        return <Quote element={element} index={index} elementAlignment={elementAlignment} />;
      case "also-read":
        return <AlsoRead story={storyTemp} element={element} elementAlignment={elementAlignment} index={index} />;
      case "image":
        return (
          <ImageLightbox
            imageSlug={element["image-s3-key"]}
            cdn={this.props.cdn}
            template={StoryElement}
            element={element}
            key={element.id}
            story={this.props.story}
          />
        );
      case "q-and-a":
        return <QuestionAnswer element={element} key={element.id} type={"q-and-a"} />;
      case "question":
        return <QuestionAnswer element={element} key={element.id} type={"question"} />;
      case "answer":
        return <QuestionAnswer element={element} key={element.id} type={"answer"} />;
      case "attachment":
        return <Attachment element={element} template={attachmentElement} key={element.id} />;
      default:
        return <StoryElement element={element} key={element.id} story={storyTemp} />;
    }
  }

  getStoryElements(storyElements) {
    const isFirstWhiteListed = (index, key) => {
      const whiteListObject = {
        0: {
          "also-read": true,
          quote: true,
          blockquote: true
        }
      };

      return get(whiteListObject, [index, key], false);
    };
    const elementAlignment = get(
      this.props,
      ["card", "metadata", "attributes", "elementalignment", 0],
      ""
    ).toLowerCase();

    const isAlignment = elementAlignment && `align-${elementAlignment}`;
    const firstElement = get(this.props, ["card", "story-elements", 0]);
    const cardStyle =
      elementAlignment && isFirstWhiteListed(0, firstElement.subtype) ? `card--${elementAlignment}` : "";
    const infiniteScrollStyle = get(this.props, ["config", "theme-attributes", "infiniteScrollStyle"], "default");
    const firstStory = this.props.firstStory;
    if (infiniteScrollStyle === "read-more" && !firstStory) {
      return (
        <div className="element-wrapper read-more-overlay">
          {(firstElement.type === "text" || firstElement.type === "title") && (
            <div key={firstElement.id} className="read-more-card read-more-overlay">
              <bdi dangerouslySetInnerHTML={{ __html: truncate(firstElement.text, 400, true) }} />
              <div className="content-overlay" />
            </div>
          )}
          <span className="read-more-btn">
            <CustomLink href={`/${get(this.props, ["story", "slug"], "")}`} rel="noopener noreferrer">
              Read More
            </CustomLink>
          </span>
        </div>
      );
    } else {
      return (
        <div styleName="elements-wrapper" className={`card ${cardStyle}`}>
          {storyElements.map((element, index) => {
            const removeMaxWidth =
              elementAlignment && isFirstWhiteListed(index, element.subtype) ? "remove-margin" : "";

            if (isFirstWhiteListed(index, element.subtype)) {
              return (
                <div className={`element-wrapper ${isAlignment} ${removeMaxWidth}`}>
                  {this.getStoryElement(element, index)}
                </div>
              );
            }
            return (
              <div key={element.id} className={`element-wrapper  ${isAlignment} ${removeMaxWidth}`}>
                {this.getStoryElement(element, index)}
              </div>
            );
          })}
        </div>
      );
    }
  }

  render() {
    const { template, card, story, config } = this.props;
    const selectTemplate = selectedStoryTemplate(template, story, config);
    const [imageElement, otherElements] = card["story-elements"].reduce(
      ([imageElement, otherElements], element) => {
        if (!imageElement && element.type === "image") {
          return [element, otherElements];
        }
        return [imageElement, [...otherElements, element]];
      },
      [null, []]
    );

    return (
      <div
        ref={cardRef => (this.cardRef = cardRef)}
        styleName={imageElement && selectTemplate === "hero-priority" ? "photo-story-card" : "photo-story-card-default"}
      >
        {imageElement ? (
          <MainImageWrapper imageElement={imageElement} {...this.props}>
            {this.getStoryElements(otherElements)}
          </MainImageWrapper>
        ) : (
          this.getStoryElements(otherElements)
        )}
      </div>
    );
  }
}

PhotoStoryCard.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  card: PropTypes.object,
  socialShareGrey: PropTypes.bool,
  primaryColor: PropTypes.string,
  cdn: PropTypes.string,
  template: PropTypes.string,
  firstStory: PropTypes.bool
};

export { PhotoStoryCard };
